import React, { useCallback, useEffect } from 'react';
import BaseDataGrid from 'common/components/base-data-grid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Box, Checkbox, Typography } from '@mui/material';
import { createStyleSheet } from 'utils/helpers';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'common/hook';
import { RootState } from 'plugins/store';
import { toast } from 'react-toastify';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { handleError } from 'utils/helpers';
import CustomPagination from 'pages/admin/campaign/components/custom-pagination';
import CampaignCheckBoxChecked from '../../campaign/components/icons/campaign-check-box-checked';
import CampaignCheckBoxUnchecked from '../../campaign/components/icons/campaign-check-box-uncheck';
import AccountGridCollumn from './account-grid-column';
import {
    IAccount,
    IListAccountsSearchForm,
    IListAccountsPayload,
    IAccountResponse,
} from 'types/account';
import ModalConfirm from 'common/components/modal-confirm';
import {
    listAccounts,
    deleteAccount,
} from 'services/accountManagement.service';
import AccountConstants from '../constants';

const AccountDataGrid: React.FC = () => {
    const navigate = useNavigate();
    const [accounts, setAccounts] = React.useState<IAccount[]>([]);
    const accountsSearchForm = useAppSelector(
        (state: RootState) => state.account.accountsSearchForm
    );
    const [openModalDeleteUser, setOpenModalDeleteUser] = React.useState(false);
    const [userSelected, setUserSelected] = React.useState<IAccount | null>();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });
    const [rowCount, setRowCount] = React.useState(0);

    const transformParams = (
        formState: IListAccountsSearchForm,
        page: number
    ): IListAccountsPayload => {
        const params = { ...formState };
        if (formState.status?.includes(AccountConstants.status.ALL)) {
            params.status = [];
        }
        if (formState.type?.includes(AccountConstants.type.ALL)) {
            params.type = [];
        }
        return {
            page: page,
            q: {
                search_with_key_cont: params.searchKeyWord,
                status_in: params.status?.join(','),
                role_in: params.type?.join(','),
                start_date: params.startDate ?? null,
                end_date: params.endDate ?? null,
            },
        };
    };
    const fetchData = useCallback(
        async (page = 1) => {
            try {
                const response = await listAccounts(
                    transformParams(accountsSearchForm, page)
                );
                if (!response.success) {
                    toast.error(response.message);
                    return;
                }
                setAccounts(
                    response.data.admin_managements.map(
                        (item: IAccountResponse) => {
                            const { attributes } = item;
                            return {
                                id: attributes.id,
                                name: attributes.username,
                                email: attributes.email,
                                type: attributes.role,
                                status: attributes.status,
                                createdAt: attributes.created_at,
                                is_delete: attributes.is_delete,
                            };
                        }
                    )
                );
                setRowCount(response.data.total_items);
            } catch (error) {
                handleError(error);
            }
        },
        [accountsSearchForm]
    );

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onClickGridViewIconButton = (row: IAccount) => {
        navigate(`/admin/account-management/${row.id}`);
    };
    const onClickDeleteRow = (row: IAccount) => {
        setUserSelected(row);
        setOpenModalDeleteUser(true);
    };
    const onClickEditRow = (row: IAccount) => {
        navigate(`/admin/account-management/${row.id}/edit`);
    };

    const handleConfirmDeleteAccount = async () => {
        if (!userSelected) {
            return;
        }
        const response = await deleteAccount(userSelected.id);
        if (!response.success) {
            toast.error(response.message);
            return;
        }
        toast.success(response.message);
        setOpenModalDeleteUser(false);
        setAccounts(accounts.filter((item) => item.id !== userSelected.id));
    };
    const columns: GridColDef[] = AccountGridCollumn(
        onClickGridViewIconButton,
        onClickDeleteRow,
        onClickEditRow
    );

    function labelDisplayedRows({
        from,
        to,
        count,
    }: {
        from: number;
        to: number;
        count: number;
    }) {
        return `Xem ${from} - ${to} trên ${count !== -1 ? count : `hơn ${to}`}`;
    }
    const onCellClick = (cell: GridCellParams) => {
        if (cell.field === 'actions' || cell.field === '__check__') return;
        onClickEditRow(cell.row as IAccount);
    };
    return (
        <Box component="div">
            <BaseDataGrid
                onCellClick={(cell) => onCellClick(cell)}
                columns={columns}
                rows={accounts}
                checkboxSelection
                disableRowSelectionOnClick
                disableColumnMenu
                autoHeight
                sx={styles.dataGrid}
                rowHeight={83}
                localeText={{
                    MuiTablePagination: {
                        labelDisplayedRows,
                    },
                    footerRowSelected: (count) => (
                        <Typography component="span" fontSize={'16px'}>
                            {count} hàng đã chọn
                        </Typography>
                    ),
                }}
                slots={{
                    pagination: CustomPagination,
                    noRowsOverlay: () => (
                        <Box
                            component="div"
                            display="flex"
                            justifyContent="center"
                            alignItems="center"
                            height={'100%'}
                        >
                            <Typography component="span" fontSize={'16px'}>
                                Không có dữ liệu
                            </Typography>
                        </Box>
                    ),
                    baseCheckbox: (props) => (
                        <Checkbox
                            {...props}
                            checkedIcon={
                                <CampaignCheckBoxChecked
                                    sx={{
                                        color: (theme) =>
                                            theme.palette.text.primary,
                                    }}
                                />
                            }
                            icon={<CampaignCheckBoxUnchecked />}
                            indeterminateIcon={
                                <IndeterminateCheckBoxIcon
                                    style={{
                                        width: '20px',
                                        height: '20px',
                                    }}
                                />
                            }
                        />
                    ),
                }}
                initialState={{
                    pagination: { paginationModel: { pageSize: 10 } },
                }}
                pageSizeOptions={[]}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={(newModel) => {
                    setPaginationModel(newModel);
                    fetchData(newModel.page + 1);
                }}
                rowCount={rowCount}
            />
            {openModalDeleteUser && (
                <ModalConfirm
                    open={openModalDeleteUser}
                    onClose={() => setOpenModalDeleteUser(false)}
                    title={'Xác nhận xóa tài khoản'}
                    textOk="Xóa"
                    onOk={handleConfirmDeleteAccount}
                    content={
                        <Box>
                            Bạn có muốn chắc chắn xóa tài khoản{' '}
                            <b>{`" ${userSelected?.email}"`}</b> này không?
                        </Box>
                    }
                />
            )}
        </Box>
    );
};

const styles = createStyleSheet({
    dataGrid: {
        '--DataGrid-rowBorderColor': 'transparent',
        '.MuiDataGrid-columnHeaders': {
            height: '83px',
            justifyContent: 'center',
        },
        '.MuiDataGrid-columnHeader': {
            padding: 0,
        },
        '.MuiDataGrid-columnHeaderTitle': {
            fontSize: '20px',
            lineHeight: '24px',
            fontWeight: 700,
        },
        '.MuiDataGrid-columnHeaderTitleContainer': {
            display: 'flex',
        },
        '.MuiDataGrid-cell': {
            outline: 'none !important',
            padding: '0',
        },
        '.MuiDataGrid-footerContainer': {
            borderColor: 'transparent',
            marginBottom: '44px',
            marginRight: '54px',
        },
        '.MuiTablePagination-displayedRows': {
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '17px',
        },
        border: 'none',
    },
});

export default AccountDataGrid;
